import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { LoaderService } from '../../core/services/loader.service';
import { ManageSettingsService } from '../manage-settings/manage-settings.service';
import { CommonService } from '../../shared/services/common.service';
import { ErrorResponse } from '../../core/models/error.models';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ReferenceMaterials } from '../manage-settings/manage-settings.interface';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { OrganizationDetailModel } from '../organizations/organization.interface';
import { OrganizationSelectionService } from '../../core/services/organization-selection.service';

interface HubSpotConversationsApi {
  widget: {
    load(options?: { widgetOpen?: boolean }): void;
    open(): void;
    remove(): void;
  };
}

declare global {
  interface Window {
    HubSpotConversations?: HubSpotConversationsApi;
    hsConversationsOnReady?: (() => void)[];
  }
}

interface ContactUsCard {
  label: string;
  value: string;
}

type ReferenceDownload = Pick<ReferenceMaterials, 'name' | 'fileUrl'>;

const LIVECHAT_CARD_LABEL = 'Live Chat';
const SUPPORT_PHONE = '888-542-4265';
const SUPPORT_EMAIL = 'support@insightstobehavior.com';
const USER_GUIDE_URL =
  'https://learn.insightstobehavior.com/hubfs/User%20Guide.docx';

@Component({
  selector: 'app-support',
  standalone: true,
  imports: [
    NzCollapseModule,
    NzButtonModule,
    SvgIconComponent,
    NzIconModule,
    CommonModule,
    NzSpinModule,
    NzButtonModule,
  ],
  templateUrl: './support.component.html',
  styleUrl: './support.component.scss',
})
export class SupportComponent implements OnDestroy {
  organizationId = 4519;
  referenceMaterials: ReferenceMaterials[] = [];
  contactUsCards: ContactUsCard[] = [
    { label: LIVECHAT_CARD_LABEL, value: 'Need Help?' },
    { label: 'Phone', value: SUPPORT_PHONE },
    { label: 'Email', value: SUPPORT_EMAIL },
  ];
  referenceDownloads: ReferenceDownload[] = [
    { name: 'User Guide', fileUrl: USER_GUIDE_URL },
  ];
  panels = [
    {
      active: true,
      disabled: false,
      name: 'District Reference Materials',
      content: this.referenceMaterials,
    },
    {
      active: false,
      disabled: false,
      name: 'Reference Downloads',
      content: this.referenceDownloads,
    },
    {
      active: false,
      disabled: true,
      name: 'Behavior Management',
    },
    {
      active: false,
      disabled: false,
      name: 'E-learning',
    },
    {
      active: false,
      disabled: false,
      name: 'Contact Us',
      cards: this.contactUsCards,
    },
  ];
  selectedPanel: boolean[] = [];
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean>;
  orgData: OrganizationDetailModel | null = null;
  constructor(
    private readonly loaderService: LoaderService,
    private readonly manageSettingService: ManageSettingsService,
    private readonly commonService: CommonService,
    private readonly orgSelectionService: OrganizationSelectionService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.orgSelectionService.parentOrgId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgId => {
        if (orgId === null) {
          return;
        }
        this.organizationId = orgId;
        this.loadReferenceMaterials();
      });
    for (const ele of this.panels) {
      this.selectedPanel.push(ele.active);
    }
  }

  private loadReferenceMaterials(): void {
    this.loaderService.show();
    this.manageSettingService
      .getReferenceMaterials({
        pageIndex: 1,
        pageSize: 500,
        organizationId: this.organizationId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.result) {
            this.referenceMaterials = response.result.items;
            this.panels[0].content = this.referenceMaterials;
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.HubSpotConversations?.widget.remove();
  }

  downloadFile(attachment: ReferenceDownload): void {
    window.open(attachment.fileUrl, '_blank');
  }

  onPanelChange(index: number, isActive: boolean): void {
    this.panels[index].active = isActive;
    this.selectedPanel[index] = isActive;
    if (isActive && this.panels[index].name === 'Contact Us') {
      this.loadLiveChatWidget();
    }
  }

  isLiveChatCard(label: string): boolean {
    return label === LIVECHAT_CARD_LABEL;
  }

  onCardSpaceKeyDown(event: Event, label: string): void {
    if (!this.isLiveChatCard(label)) {
      return;
    }
    event.preventDefault();
    this.openLiveChat();
  }

  openLiveChat(): void {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.open();
      return;
    }
    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(() => {
      window.HubSpotConversations?.widget.load({ widgetOpen: true });
    });
  }

  private loadLiveChatWidget(): void {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.load();
      return;
    }
    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(() => {
      window.HubSpotConversations?.widget.load();
    });
  }
}
